// components/products/ProductForm.tsx
import { useState, useEffect } from 'react';
import { Image as ImageIcon, Trash2, Plus, X } from 'lucide-react';
import { Product, ProductFormData } from '../../../types/Product';

interface ProductFormProps {
  initialData?: Partial<Product>;
  onSubmit: (data: ProductFormData) => Promise<void>;
  isSubmitting?: boolean;
}

interface FormErrors {
  name?: string;
  type?: string;
  price?: string;
  quality?: string;
  density?: string;
  texture?: string;
  defaultLength?: string;
  lengths?: string;
  image?: string;
}

const HAIR_LENGTHS = [
  '14 pouces',
  '16 pouces', 
  '18 pouces',
  '20 pouces',
  '22 pouces',
  '24 pouces',
  '26 pouces',
  '28 pouces',
  '30 pouces'
];

const QUALITIES = [
  'Raw Virgin Hair 10A',
  'Raw Virgin Hair 11A', 
  'Raw Virgin Hair 12A',
  'Raw Virgin Hair 13A',
  'Premium Virgin Hair',
  'Standard Virgin Hair'
];

const DENSITIES = ['130%', '150%', '180%', '200%', '220%', '250%'];

const TEXTURES = [
  'Straight',
  'Body Wave',
  'Deep Curly',
  'Tight Curly',
  'Yaki Straight',
  'Kinky Straight',
  'Water Wave',
  'Loose Wave'
];

const PRODUCT_TYPES = [
  'Lace Frontal',
  'Lace Closure',
  'HD Lace Frontal',
  'HD Lace Closure',
  'Wig HD',
  'Wig Standard',
  'Bundle Pack',
  'Single Bundle',
  'Full Lace Wig',
  'Frontal Wig'
];

export default function ProductForm({
  initialData,
  onSubmit,
  isSubmitting = false
}: ProductFormProps) {
  const [formData, setFormData] = useState<ProductFormData>({
    id: initialData?.id || '',
    name: initialData?.name || '',
    price: initialData?.price || 0,
    description: initialData?.description || '',
    type: initialData?.type || '',
    lengths: initialData?.lengths || ['18 pouces'],
    defaultLength: initialData?.defaultLength || '18 pouces',
    image: new File([], ''), // Champ requis par l'interface, mais vide par défaut
    badge: initialData?.badge || '',
    quality: initialData?.quality || 'Raw Virgin Hair 12A',
    density: initialData?.density || '180%',
    texture: initialData?.texture || 'Body Wave'
  });

  // L'URL de l'image existante provenant de la DB
  const [existingImageUrl, setExistingImageUrl] = useState<string>(initialData?.imageUrl || '');
  
  // Le nouveau fichier image à uploader
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  
  // L'URL de prévisualisation de la nouvelle image
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('');
  
  const [newLength, setNewLength] = useState<string>('');
  const [errors, setErrors] = useState<FormErrors>({});

  // Gestionnaire pour l'aperçu de l'image
  useEffect(() => {
    if (newImageFile) {
      const url = URL.createObjectURL(newImageFile);
      setImagePreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [newImageFile]);

  // Gestion des changements de champs texte
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Gestion des nombres
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Gestion de l'upload d'image
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, image: 'L\'image ne doit pas dépasser 5MB' }));
        return;
      }
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, image: 'Format de fichier non supporté' }));
        return;
      }
      setNewImageFile(file);
      setFormData(prev => ({ ...prev, image: file }));
      setErrors(prev => ({ ...prev, image: undefined }));
    }
  };

  // Supprimer l'image existante (celle de la DB)
  const removeExistingImage = () => {
    setExistingImageUrl('');
  };

  // Supprimer la nouvelle image (celle uploadée)
  const removeNewImage = () => {
    setNewImageFile(null);
    setImagePreviewUrl('');
    setFormData(prev => ({ ...prev, image: new File([], '') }));
  };

  // Gestion des longueurs
  const addLength = () => {
    if (newLength && !formData.lengths.includes(newLength)) {
      setFormData(prev => ({
        ...prev,
        lengths: [...prev.lengths, newLength]
      }));
      setNewLength('');
      if (errors.lengths) {
        setErrors(prev => ({ ...prev, lengths: undefined }));
      }
    }
  };

  const removeLength = (lengthToRemove: string) => {
    if (formData.lengths.length <= 1) {
      setErrors(prev => ({ ...prev, lengths: 'Au moins une longueur requise' }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      lengths: prev.lengths.filter(l => l !== lengthToRemove),
      defaultLength: prev.defaultLength === lengthToRemove ? prev.lengths.filter(l => l !== lengthToRemove)[0] || '' : prev.defaultLength
    }));
  };

  // Validation du formulaire
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Nom du produit requis';
    if (!formData.type) newErrors.type = 'Type de produit requis';
    if (formData.price <= 0) newErrors.price = 'Prix requis et supérieur à 0';
    if (!formData.quality) newErrors.quality = 'Qualité requise';
    if (!formData.density) newErrors.density = 'Densité requise';
    if (!formData.texture) newErrors.texture = 'Texture requise';
    if (!formData.defaultLength) newErrors.defaultLength = 'Longueur par défaut requise';
    if (formData.lengths.length === 0) newErrors.lengths = 'Au moins une longueur requise';
    
    // Vérification de l'image : soit une image existante en DB, soit une nouvelle uploadée
    if (!existingImageUrl && !newImageFile) {
      newErrors.image = 'Image du produit requise';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData: ProductFormData = {
      ...formData,
      image: newImageFile || new File([], '') // Si nouvelle image, on l'envoie, sinon vide
    };

    await onSubmit(submitData);
  };

  // Rendu du champ de sélection des longueurs
  const renderLengthSelector = () => (
    <div className="space-y-3">
      <div className="flex gap-2">
        <select
          value={newLength}
          onChange={(e) => setNewLength(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
        >
          <option value="">Sélectionner une longueur</option>
          {HAIR_LENGTHS
            .filter(l => !formData.lengths.includes(l))
            .map(length => (
              <option key={length} value={length}>{length}</option>
            ))
          }
        </select>
        <button
          type="button"
          onClick={addLength}
          disabled={!newLength}
          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {formData.lengths.map((length) => (
          <span
            key={length}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
              length === formData.defaultLength
                ? 'bg-orange-100 text-orange-800 border-2 border-orange-500'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {length}
            {length === formData.defaultLength && (
              <span className="text-xs font-medium text-orange-600">(Défaut)</span>
            )}
            <button
              type="button"
              onClick={() => removeLength(length)}
              className="hover:text-red-600 transition"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
      </div>
      
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span>Longueur par défaut :</span>
        <select
          value={formData.defaultLength}
          onChange={(e) => setFormData(prev => ({ ...prev, defaultLength: e.target.value }))}
          className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
        >
          {formData.lengths.map(length => (
            <option key={length} value={length}>{length}</option>
          ))}
        </select>
      </div>
      
      {errors.lengths && (
        <p className="text-red-500 text-xs">{errors.lengths}</p>
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Image du produit */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Image du produit *
        </label>
        <div className="flex items-center gap-4">
          {/* Afficher l'image existante (de la DB) ou la nouvelle prévisualisation */}
          {(existingImageUrl || imagePreviewUrl) ? (
            <div className="relative w-40 h-40 rounded-lg overflow-hidden border border-gray-200">
              <img
                src={imagePreviewUrl || existingImageUrl}
                alt="Product"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => {
                  if (imagePreviewUrl) {
                    removeNewImage(); // Supprimer la nouvelle image
                  } else {
                    removeExistingImage(); // Supprimer l'image existante de la DB
                  }
                }}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="w-40 h-40 rounded-lg border-2 border-dashed border-gray-300 hover:border-orange-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-2 bg-gray-50 hover:bg-orange-50">
              <ImageIcon className="w-8 h-8 text-gray-400" />
              <span className="text-xs text-gray-500 text-center">Cliquez pour ajouter une image</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </label>
          )}
        </div>
        {errors.image && (
          <p className="text-red-500 text-xs mt-1">{errors.image}</p>
        )}
        <p className="text-xs text-gray-500 mt-2">Formats: JPG, PNG, WebP. Max 5MB</p>
      </div>

      {/* Informations générales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom du produit *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${
              errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Ex: Lace Frontal Raw Virgin Hair 12A"
          />
          {errors.name && (
            <p className="text-red-500 text-xs mt-1">{errors.name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type de produit *
          </label>
          <select
            name="type"
            value={formData.type}
            onChange={handleInputChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${
              errors.type ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">Sélectionner un type</option>
            {PRODUCT_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          {errors.type && (
            <p className="text-red-500 text-xs mt-1">{errors.type}</p>
          )}
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          name="description"
          rows={4}
          value={formData.description}
          onChange={handleInputChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
          placeholder="Description détaillée du produit, caractéristiques, avantages..."
        />
      </div>

      {/* Qualité, Densité, Texture */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Qualité *
          </label>
          <select
            name="quality"
            value={formData.quality}
            onChange={handleInputChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${
              errors.quality ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            {QUALITIES.map(quality => (
              <option key={quality} value={quality}>{quality}</option>
            ))}
          </select>
          {errors.quality && (
            <p className="text-red-500 text-xs mt-1">{errors.quality}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Densité *
          </label>
          <select
            name="density"
            value={formData.density}
            onChange={handleInputChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${
              errors.density ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            {DENSITIES.map(density => (
              <option key={density} value={density}>{density}</option>
            ))}
          </select>
          {errors.density && (
            <p className="text-red-500 text-xs mt-1">{errors.density}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Texture *
          </label>
          <select
            name="texture"
            value={formData.texture}
            onChange={handleInputChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${
              errors.texture ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            {TEXTURES.map(texture => (
              <option key={texture} value={texture}>{texture}</option>
            ))}
          </select>
          {errors.texture && (
            <p className="text-red-500 text-xs mt-1">{errors.texture}</p>
          )}
        </div>
      </div>

      {/* Longueurs */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Longueurs disponibles *
        </label>
        {renderLengthSelector()}
      </div>

      {/* Prix */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Prix (FCFA) *
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">FCFA</span>
          <input
            type="number"
            name="price"
            value={formData.price || ''}
            onChange={handleNumberChange}
            className={`w-full pl-16 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${
              errors.price ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="95 000"
          
          />
        </div>
        {errors.price && (
          <p className="text-red-500 text-xs mt-1">{errors.price}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">Prix en FCFA (Franc CFA)</p>
      </div>

      {/* Badge (optionnel) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Badge (optionnel)
        </label>
        <input
          type="text"
          name="badge"
          value={formData.badge || ''}
          onChange={handleInputChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
          placeholder="Ex: Promotion, Nouveau, Top Vente..."
        />
      </div>

      {/* Boutons d'action */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Enregistrement...
            </span>
          ) : (
            initialData?.id ? 'Modifier le produit' : 'Créer le produit'
          )}
        </button>
      </div>
    </form>
  );
}